import { useEffect } from "react";
import { useMetaTag } from "@/state/cacheStoreHooks";
import { getStrapiMediaUrl } from "@/utils/strapiUtils";

const DEFAULT_TITLE = "Cash CTRL";
const DEFAULT_DESCRIPTION = "Cash CTRL";

/**
 * Applies Strapi meta tag data to the document head.
 * Sets document.title, meta description, and Open Graph tags for SEO and social sharing.
 * Runs when metaTag is available from cache (no extra dependency on react-helmet).
 */
export function useDocumentMeta() {
    const metaTag = useMetaTag();

    useEffect(() => {
        if (!metaTag) return;

        const title = metaTag.titleTag?.trim() || DEFAULT_TITLE;
        const description = metaTag.metaDescription?.trim() || DEFAULT_DESCRIPTION;
        const ogImageUrl = metaTag.sharePreviewImage?.url
            ? getStrapiMediaUrl(metaTag.sharePreviewImage.url)
            : "";

        document.title = title;

        setMetaContent("name", "description", description);
        setMetaContent("property", "og:title", title);
        setMetaContent("property", "og:description", description);
        if (ogImageUrl) {
            setMetaContent("property", "og:image", ogImageUrl);
        }

        return () => {
            document.title = DEFAULT_TITLE;
            setMetaContent("name", "description", DEFAULT_DESCRIPTION);
            setMetaContent("property", "og:title", DEFAULT_TITLE);
            setMetaContent("property", "og:description", DEFAULT_DESCRIPTION);
            setMetaContent("property", "og:image", "");
        };
    }, [metaTag]);
}

function setMetaContent(
    attribute: "name" | "property",
    key: string,
    content: string,
) {
    const selector = `meta[${attribute}="${key}"]`;
    let el = document.querySelector<HTMLMetaElement>(selector);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attribute, key);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
}
